import { FC, useEffect } from 'react'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { observer } from 'mobx-react'
import { useTranslation } from 'next-i18next'
import { useStores } from '@/utils'
import { TextAreaGroup } from '@components/molecules/TextAreaGroup'
import { SettingButton } from '@components/atoms/SettingButton'
import { IAnswerBase, MessageType } from '@/types'
import styles from './index.module.scss'

type Props = {
  reply: IAnswerBase
  replies: IAnswerBase[]
  setReplies: (answers: IAnswerBase[]) => void
  clickCancel: () => void
  setEdit: (isEdit: boolean) => void
}

type Value = {
  body: string
}

export const UpdateReplyForm: FC<Props> = observer(({ reply, replies, setReplies, clickCancel, setEdit }) => {
  const { t } = useTranslation(['p', 'validation'])
  const { messages, viewer } = useStores()
  const initialValue: Value = {
    body: reply.body,
  }

  const validationSchema = Yup.object().shape({
    body: Yup.string().required(t('validation:必須項目です')),
  })

  return (
    <div className={styles.container}>
      <Formik
        initialValues={initialValue}
        validationSchema={validationSchema}
        onSubmit={async (values: Value, actions) => {
          const output = await viewer.viewer?.updateMyAnswer(reply.id, values.body)
          actions.setSubmitting(false)
          if (output.isSuccessful) {
            const newReplies = replies.map((r) => {
              if (r.id === output.data.answer.id) {
                return output.data.answer
              }
              return r
            })
            setReplies(newReplies)
            messages.add({
              type: MessageType.Info,
              body: 'Your reply changed.',
              isTranslated: true,
              ttl: 5000,
            })
            actions.resetForm()
            setEdit(false)
          }
        }}
      >
        {({ isSubmitting, setFieldValue }) => {
          useEffect(() => {
            if (!reply) {
              return
            }

            setFieldValue('body', reply?.body)
          }, [reply])
          return (
            <Form>
              <div className={styles.textarea}>
                <Field
                  name='body'
                  placeholder='Leave your reply'
                  fieldType='setting'
                  isSmall
                  component={TextAreaGroup}
                />
              </div>
              <div className={styles.buttonContainer}>
                <SettingButton
                  color='white'
                  buttonType='reset'
                  buttonSize='medium'
                  disabled={isSubmitting}
                  onClick={clickCancel}
                >
                  Cancel
                </SettingButton>
                <SettingButton
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                  color='aqa'
                  buttonType='submit'
                  buttonSize='medium'
                >
                  Save
                </SettingButton>
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
})
